import { Component, input, computed } from '@angular/core';
import type { DefautsPrecharges } from '../../Services/installation';
import { ChiffresUtilesInstallation, Installation, Tag } from '../../Services/EntiteProjet';
import { statusLabel, statusClass, formaterDateNumeriquePlusHeure } from '../../Services/format';
import {
  QM_PRODUCTION,
  QM_PUISSANCE_CRETE,
  QuickMetricItem,
  formatProductionInstantanee,
  formatPuissanceCrete,
  tooltipProductionInstantanee,
} from '../quick-metrics-grid/quick-metrics-grid';
import {
  DeieItem,
  InstallationDeiePanelComponent,
} from '../installation-deie-panel/installation-deie-panel';
import { InstallationStatutPanelComponent } from '../installation-statut-panel/installation-statut-panel';
import { InstallationTicketPanelComponent } from '../installation-ticket-panel/installation-ticket-panel';
import { SpinnerOverlayComponent } from '../spinner-overlay/spinner-overlay';
import { InstallationEquipementsPanelComponent } from '../installation-equipements-panel/installation-equipements-panel';

@Component({
  selector: 'app-installation-etat-panel',
  imports: [
    SpinnerOverlayComponent,
    InstallationTicketPanelComponent,
    InstallationDeiePanelComponent,
    InstallationStatutPanelComponent,
    InstallationEquipementsPanelComponent,
  ],
  templateUrl: './installation-etat-panel.html',
  styleUrl: './installation-etat-panel.css',
})
export class InstallationEtatPanelComponent {
  readonly chargement = input(false);
  readonly defautsPrecharges = input<DefautsPrecharges | null>(null);
  readonly erreurChargement = input('');
  readonly installation = input<Installation | null>(null);
  readonly chiffresUtiles = input<ChiffresUtilesInstallation | null>(null);

  readonly statusLabel = statusLabel;
  readonly statusClass = statusClass;

  readonly quickMetrics = computed<QuickMetricItem[]>(() => {
    const pc = this.installation()?.pc_affichee;
    const production = this.installation()?.productionInstantannee?.value;
    const dateProduction = this.installation()?.productionInstantannee?.date;
    const metrics: QuickMetricItem[] = [
      { ...QM_PUISSANCE_CRETE, value: pc != null ? formatPuissanceCrete(pc) : '—' },
    ];
    if (production != null && Number.isFinite(production)) {
      metrics.push({
        ...QM_PRODUCTION,
        value: formatProductionInstantanee(production),
        tooltip: tooltipProductionInstantanee(dateProduction, production),
      });
    }
    return metrics;
  });

  private readonly DEIE_LABELS: [string, string][] = [
    ['TS_AUTORISATION_COUPLAGE', 'Autorisation couplage'],
    ['TS_CENTRALE_COUPLEE', 'Centrale couplée'],
    ['TS_CENTRALE_INDISPONIBLE', 'Centrale indisponible'],
    ['TC_DEMANDE_DECOUPLAGE', 'Demande de découplage'],
    ['TC_EFFACEMENT_URGENCE', "Effacement d'urgence"],
    ['CENTRALE_ARRETEE', 'Centrale arrêtée'],
    ['AGREG_DECOUPLAGE', "Arrêt par l'agrégateur"],
    ['REGIE_DECOUPLAGE', 'Arrêt par la régie'],
  ];

  private readonly deie = computed(() => {
    const d = this.installation()?.DEIE;
    return d && !Array.isArray(d) ? d : null;
  });

  readonly afficherDeie = computed<boolean>(() => {
    return !!this.deie();
  });

  readonly deieDate = computed<string>(() => {
    const raw = this.deie()?.date;
    if (!raw) return '';
    const date = new Date(raw.replace(' ', 'T').replace(/([+-]\d{2})$/, '$1:00'));
    return Number.isNaN(date.getTime()) ? '' : formaterDateNumeriquePlusHeure(date);
  });

  readonly deieItems = computed<DeieItem[]>(() => {
    const etat = this.deie()?.etat;
    if (!etat) return [];
    return this.DEIE_LABELS.filter(([key]) => key in etat).map(([key, libelle]) => ({
      libelle,
      actif: etat[key],
    }));
  });

  /** Retourne les tags de l'installation */
  readonly tagsAffiches = computed<Tag[]>(() => {
    const tags = (this.installation()?.tags ?? []).filter(
      (tag): tag is Tag =>
        !!tag && typeof tag.libelle === 'string' && tag.libelle.trim().length > 0,
    );
    const tagsLies = tags.filter((tag) => tag.lie?.toLowerCase() === 't');
    return tagsLies.length > 0 ? tagsLies : tags;
  });
}
